import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import { ExperimentsService } from 'services/experiments';

import { getPaymentMethod } from 'common/source/utilities/policyInfoHelper';
import { PermissionService } from 'services/permission';
import { get } from 'lodash';
import { ModalService } from 'common/source/services/modal';
import { HippoApiService } from 'services/hippoApi';
import { ng } from '@uirouter/angularjs/lib/angular';
import { IRootScopeService } from 'angular';

const module = angular.module('payment-method-section', []);

enum PaymentMethods {
    cc = 'cc',
    escrow = 'escrow',
    ach = 'ach'
}

const PAYMENT_METHOD_MAP: PaymentMethodMap = {
    cc: 'cc-payment',
    escrow: 'escrow-payment',
    ach: 'ach-payment'
};
type PaymentMethodText = 'escrow-payment' | 'cc-payment' | 'ach-payment';

type PaymentMethodMap = { [key in PaymentMethod]: PaymentMethodText };

type paymentInEditMode = PaymentMethod | '';

interface PaymentMethodSectionController extends angular.IStrictController {
    policyInfo: CustomerPolicyInfo;
    selectedPaymentMethod: PaymentMethod;
    showSwitchPaymentMethod: boolean;
    hasUpcomingPay: () => boolean;
    isACHEnabled: boolean;
    clickEdit: (name: PaymentMethod) => void;
    clickAdd: (name: PaymentMethod) => void;
    initialAch: () => void;
    initialCreditCard: () => void;
    hasEscrowInfo: () => boolean;
    hasCreditCardInfo: () => boolean;
    hasACHInfo: () => boolean;
    paymentInEditMode: paymentInEditMode;
    isMobile: boolean;
    hoverIn: ($event: CustomEvent) => void;
    hoverOut: () => void;
    hoverEditPaymentMethod: string;
    showEditBtn: (name: PaymentMethod) => boolean;
    showDeleteBtn: (name: PaymentMethod) => boolean;
    showAddBtn: (name: PaymentMethod) => boolean;
    isInDisabledMode: (name: PaymentMethod) => boolean;
    isSelected: (name: PaymentMethod) => boolean;
    isInEditMode: (name: PaymentMethod) => boolean;
    closeEditMode: () => void;
    updatePaymentMethod: (name: PaymentMethod) => void;
    getPaymentMethodOptions: () => Array<string>;
    getValueToShow: (op: PaymentMethod) => string;
    getTitleForEditMode: (name: PaymentMethod) => string;
    onPaymentMethodChange: (name: PaymentMethod) => void;
    selectPaymentMethod: (name: PaymentMethod) => void;
    openModal: (name: PaymentMethod, type: actionType) => void;
    desktopClickAction: (name: PaymentMethod, type: actionType) => void;
    changePaymentMethod: ($event: ng.IAngularEvent, name: PaymentMethod) => void;
    getCcExpirationInfo: (paymentMethod: PaymentMethod) => string;
}

interface CustomCurrentTarget extends EventTarget {
    name: PaymentMethod;
    id: string;
}

type actionType = 'edit' | 'add';

type paymentMethodMap = { [key in PaymentMethod]: string };

const paymentMethodMap: paymentMethodMap = {
    ach: 'Bank Account (ACH)',
    cc: 'Credit Card',
    escrow: 'Mortgage Bill'
};

module.component('paymentMethodSection', {
    bindings: {
        policyInfo: '<'
    },
    template: template,
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: PaymentMethodSectionController,
        experimentsService: ExperimentsService,
        permissionService: PermissionService,
        $scope: angular.IScope,
        modalService: ModalService,
        hippoApiService: HippoApiService,
        $window: angular.IWindowService,
        $rootScope: IRootScopeService
    ) {
        this.$onInit = () => {
            this.selectedPaymentMethod = getPaymentMethod(this.policyInfo);
            this.paymentInEditMode = '';
            this.showSwitchPaymentMethod = permissionService.canEditPaymentMethod(this.policyInfo);
            $scope.$on('closePaymentEditSection', this.closeEditMode);
            $rootScope.$on('closePaymentEditSection', this.closeEditMode);
            $scope.$on('paymentMethodUpdateSuccess', this.changePaymentMethod);
            this.isMobile = $window.innerWidth < 480;
        };

        this.changePaymentMethod = (event: ng.IAngularEvent, name: PaymentMethod) => {
            this.updatePaymentMethod(name);
        };

        this.getPaymentMethodOptions = () => {
            return Object.keys(paymentMethodMap);
        };

        const getTitleForEditMode = (name: PaymentMethod) => {
            const titleMap = {
                cc: 'Edit Credit Card',
                ach: 'Edit Bank Account (ACH)',
                escrow: 'Edit Lender'
            };
            return titleMap[name];
        };

        this.getValueToShow = (name: PaymentMethod) => {
            const paymentInfoToShow = {
                cc: get(this.policyInfo.checkout_data, 'cc_info.last4'),
                escrow: get(this.policyInfo.checkout_data, 'lender_name'),
                ach: get(this.policyInfo.checkout_data, 'ach_info.last4')
            };

            const paymentInfo = !this.isInEditMode(name) && paymentInfoToShow[name];

            return `${this.isInEditMode(name) ? getTitleForEditMode(name) : paymentMethodMap[name]}${
                paymentInfo ? ' - ' + paymentInfo : ''
            }`;
        };

        this.getCcExpirationInfo = (paymentMethod: PaymentMethod) => {
            if (paymentMethod !== PaymentMethods.cc || this.isInEditMode(paymentMethod)) {
                return '';
            }

            const ccExpMonth = get(this.policyInfo.checkout_data, 'cc_info.exp_month');
            const ccExpYear = get(this.policyInfo.checkout_data, 'cc_info.exp_year');

            if (ccExpMonth && ccExpYear) {
                // only show last two digits of the expiry year.
                return `Exp: ${ccExpMonth}/${ccExpYear % 100}`;
            }
            return '';
        };

        this.hoverIn = ($event: CustomEvent) => {
            const currentTarget = $event.currentTarget as CustomCurrentTarget;
            if (!currentTarget || !currentTarget.id || this.isMobile) {
                return;
            }
            this.hoverEditPaymentMethod = currentTarget.id;
        };

        this.hoverOut = () => {
            this.hoverEditPaymentMethod = this.isMobile ? this.hoverEditPaymentMethod : '';
        };

        const showBtnsWhenWithInfoAndNotDefaultHoverOver = (name: PaymentMethod) => {
            return (
                !this.isInDisabledMode(name) &&
                this.selectedPaymentMethod !== name &&
                this.hoverEditPaymentMethod === PAYMENT_METHOD_MAP[name]
            );
        };

        this.showEditBtn = (name: PaymentMethod) => {
            if (this.isMobile) {
                return !this.isInEditMode(name) && !this.isInDisabledMode(name);
            }
            return (
                !this.isInEditMode(name) &&
                (showBtnsWhenWithInfoAndNotDefaultHoverOver(name) || this.selectedPaymentMethod === name)
            );
        };

        this.showDeleteBtn = (name: PaymentMethod) => {
            return !this.isInEditMode(name) && showBtnsWhenWithInfoAndNotDefaultHoverOver(name);
        };

        this.showAddBtn = (name: PaymentMethod) => {
            if (this.isMobile) {
                return !this.isInEditMode(name) && this.isInDisabledMode(name);
            }
            return (
                !this.isInEditMode(name) &&
                this.hoverEditPaymentMethod === PAYMENT_METHOD_MAP[name] &&
                this.isInDisabledMode(name)
            );
        };

        this.isSelected = (name: PaymentMethod) => {
            return this.selectedPaymentMethod === name;
        };

        this.updatePaymentMethod = (name: PaymentMethod) => {
            this.selectedPaymentMethod = name;
        };

        this.selectPaymentMethod = (name: PaymentMethod) => {
            if (name === getPaymentMethod(this.policyInfo) || this.isInDisabledMode(name)) {
                return;
            }

            const policyNumber = this.policyInfo.id;

            const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                checkout_data: {
                    payment_method: name
                }
            };
            $rootScope.$emit('paymentMethodUpdateSuccess', name);
            hippoApiService
                .updateCustomerPolicy(policyNumber, policyInfoUpdate)
                .then((result) => {
                    this.updatePaymentMethod(name);
                    $rootScope.$emit('updatedCustomerPolicyInfos');
                })
                .catch(() => {
                    // TODO show error message
                });
        };

        const hasPaymentInfo = (name: PaymentMethod) => {
            const hasAchInfo = () => {
                const { last4 = '', account_holder_name = '', routing_number = '' } = this.policyInfo.checkout_data
                    .ach_info
                    ? get(this, 'policyInfo.checkout_data.ach_info')
                    : {};

                return last4 && account_holder_name && routing_number;
            };

            const hasCreditCardInfo = () => {
                const { last4 = '', exp_year = '', exp_month = '' } = this.policyInfo.checkout_data.cc_info
                    ? get(this, 'policyInfo.checkout_data.cc_info')
                    : {};
                return last4 && exp_year && exp_month;
            };

            const paymentInfoMap = {
                cc: hasCreditCardInfo,
                escrow: () => this.policyInfo.checkout_data.loan_number && this.policyInfo.checkout_data.lender_name,
                ach: hasAchInfo
            };

            return paymentInfoMap[name];
        };

        this.isInDisabledMode = (name: PaymentMethod) => {
            return !hasPaymentInfo(name)();
        };

        this.isInEditMode = (name: PaymentMethod) => {
            return this.paymentInEditMode === name;
        };

        this.onPaymentMethodChange = (name: PaymentMethod) => {
            this.selectedPaymentMethod = name;
            this.selectPaymentMethod(name);
        };

        this.closeEditMode = () => {
            this.paymentInEditMode = '';
        };

        this.clickEdit = (name: PaymentMethod) => {
            $rootScope.$emit('closePaymentEditSection');
            if (this.isMobile) {
                this.openModal(name, 'edit');
            } else {
                this.paymentInEditMode = name;
            }
        };

        this.clickAdd = (name: PaymentMethod) => {
            $rootScope.$emit('closePaymentEditSection');
            if (this.isMobile) {
                this.openModal(name, 'add');
            } else {
                this.paymentInEditMode = name;
            }
        };

        const getTitleForAddMode = (name: PaymentMethod, type: actionType) => {
            function capitalizeFirstLetter(str: string) {
                return str.charAt(0).toUpperCase() + str.slice(1);
            }

            const titleMap = {
                cc: `${capitalizeFirstLetter(type)} Credit Card`,
                ach: `${capitalizeFirstLetter(type)} Bank Account (ACH)`,
                escrow: `${capitalizeFirstLetter(type)} Lender`
            };
            return titleMap[name];
        };

        this.openModal = (name: PaymentMethod, type: actionType) => {
            modalService
                .openFullPageModal(`${name}-payment`, {
                    policyInfo: this.policyInfo,
                    title: getTitleForAddMode(name, type),
                    type: type
                })
                .closePromise.then((data: any) => {});
        };
    }
});

export { module };
